/**
 * Listing methods for FileStorageService
 */

import {
  FileCategory,
  FileListResponse,
  FolderListResponse,
} from '../../models/FileStorage';
import { API_ENDPOINTS } from '../../config/api';
import { apiClient } from '../api/apiClient';

/**
 * List files with pagination and filtering
 *
 * @param projectId - Project identifier
 * @param category - File category
 * @param page - Page number (default: 1)
 * @param pageSize - Items per page (default: 100)
 * @param cctvId - Optional CCTV ID filter (learning/test only)
 * @returns File list response with pagination info
 */
export async function listFiles(
  projectId: string | number,
  category: FileCategory,
  page: number = 1,
  pageSize: number = 100,
  cctvId?: string,
  mapId?: string
): Promise<FileListResponse> {
  try {
    const endpoint = API_ENDPOINTS.FILE_STORAGE.LIST(String(projectId), category, {
      page,
      pageSize,
      cctvId,
      mapId,
    });


    const response = await apiClient.get(endpoint);


    return response.data;
  } catch (error) {
    console.error('[FileStorageService] List files failed:', error);
    throw error;
  }
}

/**
 * Collect all file paths from a folder node recursively
 */
function collectFilePaths(folder: any, basePath: string): string[] {
  const paths: string[] = [];

  if (folder.files) {
    for (const file of folder.files) {
      paths.push(basePath ? `${basePath}/${file.name}` : file.name);
    }
  }

  if (folder.subfolders) {
    for (const sub of folder.subfolders) {
      const subPath = basePath ? `${basePath}/${sub.name}` : sub.name;
      paths.push(...collectFilePaths(sub, subPath));
    }
  }

  return paths;
}

/**
 * List all file paths within a folder (recursively)
 * Fetches each subfolder's files via API since root-level response only has counts.
 *
 * @param projectId - Project identifier
 * @param category - File category (learning or test)
 * @param folderPath - Folder path to collect files from
 * @returns Array of file paths relative to category root
 */
export async function listAllFilesInFolder(
  projectId: string,
  category: FileCategory,
  folderPath: string
): Promise<string[]> {
  try {
    // Fetch the folder with ?path= to get its files and subfolders
    const endpoint = API_ENDPOINTS.FILE_STORAGE.LIST_FOLDERS(projectId, category)
      + `?path=${encodeURIComponent(folderPath)}`;
    const response = await apiClient.get(endpoint);
    const folders = response.data.data?.folders || [];

    const targetFolder = findFolderByPath(folders, folderPath) || folders[0];
    if (!targetFolder) {
      return [];
    }

    // Collect files from this folder level
    const paths: string[] = [];
    if (targetFolder.files) {
      for (const file of targetFolder.files) {
        paths.push(`${folderPath}/${file.name}`);
      }
    }

    // Recursively collect files from subfolders (parallel)
    if (targetFolder.subfolders) {
      const subResults = await Promise.all(
        targetFolder.subfolders.map((sub: any) =>
          listAllFilesInFolder(projectId, category, sub.path)
        )
      );
      subResults.forEach(subFiles => paths.push(...subFiles));
    }

    return paths;
  } catch (error) {
    console.error('[FileStorageService] List all files in folder failed:', error);
    throw error;
  }
}

/**
 * Helper method to find a folder by path in nested structure
 */
function findFolderByPath(folders: any[], path: string): any | null {
  for (const folder of folders) {
    if (folder.path === path) {
      return folder;
    }

    // Recursively search in subfolders
    if (folder.subfolders && folder.subfolders.length > 0) {
      const found = findFolderByPath(folder.subfolders, path);
      if (found) {
        return found;
      }
    }
  }
  return null;
}

/**
 * List folders for learning/test categories with nested folder support
 *
 * @param projectId - Project identifier
 * @param category - File category (learning or test)
 * @param currentPath - Current folder path for nested navigation (optional)
 * @returns Folder list response
 */
export async function listFolders(
  projectId: string,
  category: FileCategory,
  currentPath?: string
): Promise<FolderListResponse> {
  try {
    let endpoint = API_ENDPOINTS.FILE_STORAGE.LIST_FOLDERS(projectId, category);

    // Add path query parameter if provided
    if (currentPath) {
      endpoint += `?path=${encodeURIComponent(currentPath)}`;
    }


    const response = await apiClient.get(endpoint);


    // Transform backend response to FolderListResponse for FileRepositoryViewModel
    const folders = response.data.data?.folders || [];
    const items: any[] = [];

    if (currentPath) {
      // When navigating into a folder, backend returns the target folder directly
      // Try findFolderByPath first (legacy), then fall back to folders[0] (new lightweight API)
      const targetFolder = findFolderByPath(folders, currentPath) || folders[0];

      if (targetFolder) {
        // Add subfolders as items
        if (targetFolder.subfolders) {
          targetFolder.subfolders.forEach((subfolder: any) => {
            items.push({
              name: subfolder.name,
              path: subfolder.path,
              isFolder: true,
              count: subfolder.file_count || 0,
            });
          });
        }

        // Add files as items
        if (targetFolder.files) {
          targetFolder.files.forEach((file: any) => {
            items.push({
              name: file.name,
              path: `${currentPath}/${file.name}`,
              isFolder: false,
              size: file.size,
              uploadDate: file.created_at,
            });
          });
        }
      }
    } else {
      // Root level: show top-level folders with file counts
      folders.forEach((folder: any) => {
        items.push({
          name: folder.name,
          path: folder.path,
          isFolder: true,
          count: folder.file_count || 0,
        });
      });
    }

    return {
      success: response.data.success,
      message: response.data.message,
      data: {
        items: items,
        total: items.length,
        currentPath: currentPath || '',
      },
    };
  } catch (error) {
    console.error('[FileStorageService] List folders failed:', error);
    throw error;
  }
}
